<template>
  <section class="dash-insight-grid" aria-label="汇报摘要">
    <article class="dash-card dash-card--accent">
      <div class="dash-card__header">
        <div class="dash-panel__heading">
          <span class="dash-panel__eyebrow">指导师通话榜</span>
          <h2>{{ teacherRankTitle }}</h2>
        </div>
        <p class="dash-card__text">{{ teacherRankDesc }}</p>
      </div>
      <div v-if="teacherLoading" class="insight-state">指导师榜单加载中...</div>
      <div v-else-if="teacherError" class="insight-state insight-state--error">{{ teacherError }}</div>
      <div v-else-if="!teacherRankList.length" class="insight-state">{{ emptyReason }}</div>
      <div v-else class="insight-rank-list">
        <div
          v-for="(item, index) in teacherRankList"
          :key="item.teacherName + index"
          class="insight-rank-item"
          :class="`insight-rank-item--rank-${Math.min(index + 1, 3)}`"
        >
          <span class="insight-rank-item__rank">{{ index + 1 }}</span>
          <div class="insight-rank-item__body">
            <strong>{{ item.teacherName }}</strong>
            <span>{{ teacherRankMeta(item) }}</span>
          </div>
          <span class="insight-rank-item__value">{{ item.callReachRate }}%</span>
        </div>
      </div>
    </article>

    <article class="dash-card">
      <div class="dash-section-heading">
        <div class="dash-panel__heading">
          <span class="dash-panel__eyebrow">层级触达榜</span>
          <h2>{{ moduleBTitle }}</h2>
          <span class="dash-panel__hint">按触达率由低到高排列</span>
        </div>
        <span class="dash-badge dash-badge--ghost">{{ moduleBBadge }}</span>
      </div>
      <div v-if="groupLoading" class="insight-state">层级榜单加载中...</div>
      <div v-else-if="groupError" class="insight-state insight-state--error">{{ groupError }}</div>
      <div v-else-if="!moduleBRows.length" class="insight-state">{{ emptyReason }}</div>
      <div v-else class="insight-table-scroll">
        <table class="insight-table">
          <thead>
            <tr>
              <th>排名</th>
              <th>{{ moduleBNameHeader }}</th>
              <th>负责人</th>
              <th>线索量</th>
              <th>触达率</th>
              <th>异常率</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in moduleBRows"
              :key="item.id"
              class="insight-table__row--clickable"
              :class="{ 'insight-table__row--active': item.id === activeModuleBId }"
              role="button"
              tabindex="0"
              title="点击查看下级详情"
              @click="onModuleBClick(item)"
              @keydown.enter.prevent="onModuleBClick(item)"
              @keydown.space.prevent="onModuleBClick(item)"
            >
              <td class="insight-table__rank"><strong>{{ index + 1 }}</strong></td>
              <td class="insight-table__name">{{ item.name }}</td>
              <td class="insight-table__leader">{{ item.leaderName }}</td>
              <td class="insight-table__count">{{ item.allocatedCount }}</td>
              <td>
                <span class="insight-rate" :class="rateClass(item.overallReachRate, 85, 90)">{{ item.overallReachRate }}%</span>
              </td>
              <td>
                <span class="insight-rate" :class="rateClass(item.anomalyRate || 0, 3, 8, true)">{{ item.anomalyRate != null ? item.anomalyRate + '%' : '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>

    <article class="dash-card">
      <div class="dash-section-heading">
        <div class="dash-panel__heading">
          <span class="dash-panel__eyebrow">触达明细</span>
          <h2>{{ moduleCTitle }}</h2>
        </div>
        <span class="dash-badge dash-badge--ghost">{{ moduleCBadge }}</span>
      </div>
      <div v-if="groupLoading" class="insight-state">详情加载中...</div>
      <div v-else-if="groupError" class="insight-state insight-state--error">{{ groupError }}</div>
      <div v-else-if="!moduleCRows.length" class="insight-state">{{ moduleCEmptyText }}</div>
      <div v-else class="insight-table-scroll">
        <table class="insight-table">
          <thead>
            <tr>
              <th>排名</th>
              <th>业务组</th>
              <th>负责人</th>
              <th>线索量</th>
              <th>触达率</th>
              <th>异常率</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in moduleCRows"
              :key="item.orgUnitId || item.teamName"
              class="insight-table__row--clickable"
              :class="{ 'insight-table__row--active': item.orgUnitId === selectedGroupId }"
              role="button"
              tabindex="0"
              title="点击查看该小组老师与家长"
              @click="selectGroup(item)"
              @keydown.enter.prevent="selectGroup(item)"
              @keydown.space.prevent="selectGroup(item)"
            >
              <td class="insight-table__rank"><strong>{{ index + 1 }}</strong></td>
              <td class="insight-table__name">{{ item.teamName }}</td>
              <td class="insight-table__leader">{{ item.leaderName }}</td>
              <td class="insight-table__count">{{ item.allocatedCount }}</td>
              <td>
                <span class="insight-rate" :class="rateClass(item.overallReachRate, 85, 90)">{{ item.overallReachRate }}%</span>
              </td>
              <td>
                <span class="insight-rate" :class="rateClass(item.anomalyRate || 0, 3, 8, true)">{{ item.anomalyRate != null ? item.anomalyRate + '%' : '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { getAccessibleOrgTree, getTeacherRank, getGroupRank } from '@/api'

const props = defineProps({
  campPeriodName: { type: String, default: '' },
  orgUnitId: { type: String, default: '' },
  role: { type: String, default: '' },
  scope: { type: Object, default: () => ({}) },
  selectedDeptId: { type: String, default: '' },
  selectedGroupId: { type: String, default: '' },
})

const emit = defineEmits(['selectDept', 'selectGroup', 'defaultSelectionReady'])

const teacherRankList = ref([])
const groupRankList = ref([])
const accessibleTree = ref([])
const teacherLoading = ref(false)
const groupLoading = ref(false)
const teacherError = ref('')
const groupError = ref('')
let teacherRankTimer = null
let rankLoadSeq = 0
const groupDeptMap = computed(() => buildGroupDeptMap(accessibleTree.value))
const isCenterLead = computed(() => props.role === 'CENTER_LEAD')
const isDeptLead = computed(() => props.role === 'DEPT_LEAD')
const isManager = computed(() => props.role === 'MANAGER')
const deptRankList = computed(() => {
  const buckets = new Map()
  groupRankList.value.forEach(group => {
    const deptInfo = groupDeptMap.value.get(group.orgUnitId) || {
      deptId: group.orgUnitId || group.teamName,
      deptName: '中心直属',
      leaderName: group.leaderName || '-'
    }
    const key = deptInfo.deptId
    const current = buckets.get(key) || {
      deptId: key,
      deptName: deptInfo.deptName,
      leaderName: deptInfo.leaderName || '-',
      allocatedCount: 0,
      reachedWeighted: 0,
      anomalyWeighted: 0,
      anomalyBase: 0,
    }
    const allocated = Number(group.allocatedCount || 0)
    current.allocatedCount += allocated
    current.reachedWeighted += allocated * Number(group.overallReachRate || 0)
    if (group.anomalyRate != null) {
      current.anomalyWeighted += allocated * Number(group.anomalyRate || 0)
      current.anomalyBase += allocated
    }
    buckets.set(key, current)
  })

  return [...buckets.values()]
    .map(item => ({
      ...item,
      overallReachRate: formatRate(item.allocatedCount ? item.reachedWeighted / item.allocatedCount : 0),
      anomalyRate: item.anomalyBase ? formatRate(item.anomalyWeighted / item.anomalyBase) : 0,
    }))
    .sort((a, b) => (a.overallReachRate || 0) - (b.overallReachRate || 0))
})
const groupRows = computed(() => groupRankList.value.map(group => ({
  ...group,
  id: group.orgUnitId || group.teamName,
  name: group.teamName || '未命名小组',
})))
const moduleBRows = computed(() => {
  if (isCenterLead.value) {
    return deptRankList.value.map(item => ({
      ...item,
      id: item.deptId,
      name: item.deptName,
    }))
  }
  return groupRows.value
})
const activeModuleBId = computed(() => {
  if (isCenterLead.value) return props.selectedDeptId || moduleBRows.value[0]?.id || ''
  return props.selectedGroupId || moduleBRows.value[0]?.id || ''
})
const selectedDeptName = computed(() => {
  return deptRankList.value.find(item => item.deptId === activeModuleBId.value)?.deptName || props.scope?.orgName || '部门'
})
const selectedDeptGroupRankList = computed(() => {
  if (!activeModuleBId.value) return []
  return groupRankList.value.filter(group => {
    const deptInfo = groupDeptMap.value.get(group.orgUnitId)
    const deptId = deptInfo?.deptId || group.orgUnitId || group.teamName
    return deptId === activeModuleBId.value
  })
})
const selectedGroup = computed(() => {
  return groupRankList.value.find(group => (group.orgUnitId || group.teamName) === props.selectedGroupId)
    || groupRows.value[0]
    || null
})
const groupNameDeptMap = computed(() => {
  const map = new Map()
  groupRankList.value.forEach(group => {
    const deptInfo = groupDeptMap.value.get(group.orgUnitId)
    if (group.teamName && deptInfo?.deptName) {
      map.set(group.teamName, deptInfo.deptName)
    }
  })
  return map
})
const moduleCRows = computed(() => {
  if (isCenterLead.value) return selectedDeptGroupRankList.value
  if (selectedGroup.value) return [selectedGroup.value]
  return []
})
const teacherRankTitle = computed(() => {
  if (isCenterLead.value) return '全中心 Top 5'
  if (isDeptLead.value) return '本部门 Top 5'
  if (isManager.value) return '本组 Top 5'
  return '优秀指导师 Top 5'
})
const teacherRankDesc = computed(() => {
  if (isCenterLead.value) return '按中心可见范围的通话触达率排序。'
  if (isDeptLead.value) return '按本部门各小组指导师通话触达率排序。'
  if (isManager.value) return '按本组指导师通话触达率排序。'
  return '按当前权限范围的通话触达率排序。'
})
const moduleBTitle = computed(() => {
  if (isCenterLead.value) return '部门触达'
  if (isDeptLead.value) return '小组触达'
  if (isManager.value) return '本组概览'
  return '组织触达'
})
const moduleBBadge = computed(() => isCenterLead.value ? '部门汇总' : '小组汇总')
const moduleBNameHeader = computed(() => isCenterLead.value ? '部门' : '业务组')
const moduleCTitle = computed(() => {
  if (isCenterLead.value) return `${selectedDeptName.value} · 小组`
  if (selectedGroup.value) return `${selectedGroup.value.teamName || selectedGroup.value.name}`
  return '小组详情'
})
const moduleCBadge = computed(() => isCenterLead.value ? '点击查看下方' : '跟随选中小组')
const emptyReason = computed(() => {
  if (!props.campPeriodName) return '请选择营期后查看数据'
  if (!props.scope?.orgUnitId) return '当前账号没有可访问组织'
  return '当前范围暂无数据'
})
const moduleCEmptyText = computed(() => {
  if (isCenterLead.value) return '当前部门暂无可展开小组'
  return '当前范围暂无小组详情'
})

async function fetchTeacherRank() {
  if (!props.campPeriodName) return
  teacherLoading.value = true
  teacherError.value = ''
  try {
    const params = { campPeriodName: props.campPeriodName }
    if (props.orgUnitId) params.orgUnitId = props.orgUnitId
    const res = await getTeacherRank(params)
    if (Array.isArray(res)) {
      teacherRankList.value = res
    } else if (res.code === 200 && res.data) {
      teacherRankList.value = res.data
    }
  } catch (err) {
    console.error('获取指导师榜单失败', err)
    teacherError.value = '指导师榜单加载失败，请稍后重试'
  } finally {
    teacherLoading.value = false
  }
}

async function fetchGroupRank() {
  if (!props.campPeriodName) return
  groupLoading.value = true
  groupError.value = ''
  try {
    const params = { campPeriodName: props.campPeriodName }
    if (props.orgUnitId) params.orgUnitId = props.orgUnitId
    const res = await getGroupRank(params)
    if (Array.isArray(res)) {
      groupRankList.value = res
    } else if (res.code === 200 && res.data) {
      groupRankList.value = res.data
    }
    groupRankList.value.sort((a, b) => (a.overallReachRate || 0) - (b.overallReachRate || 0))
  } catch (err) {
    console.error('获取组排名失败', err)
    groupError.value = '层级榜单加载失败，请稍后重试'
  } finally {
    groupLoading.value = false
  }
}

async function fetchAccessibleTree() {
  try {
    const res = await getAccessibleOrgTree()
    if (res.code === 200 && Array.isArray(res.data)) {
      accessibleTree.value = res.data
    }
  } catch (err) {
    console.error('获取组织树失败', err)
  }
}

watch(() => [props.campPeriodName, props.orgUnitId], async () => {
  const seq = ++rankLoadSeq
  if (teacherRankTimer) {
    clearTimeout(teacherRankTimer)
    teacherRankTimer = null
  }
  teacherRankList.value = []
  await fetchGroupRank()
  if (seq !== rankLoadSeq) return
  teacherRankTimer = setTimeout(() => {
    if (seq !== rankLoadSeq) return
    fetchTeacherRank()
  }, 300)
}, { immediate: true })

onUnmounted(() => {
  if (teacherRankTimer) clearTimeout(teacherRankTimer)
})

watch(moduleBRows, (list) => {
  if (!list.length) return
  if (isCenterLead.value) {
    if (props.selectedDeptId && list.some(item => item.id === props.selectedDeptId)) return
    const current = list.find(item => item.id === props.selectedDeptId) || list[0]
    emit('selectDept', toDeptPayload(current))
    return
  }
  if (props.selectedGroupId && list.some(item => item.id === props.selectedGroupId)) return
  const current = list.find(item => item.id === props.selectedGroupId) || list[0]
  selectGroup(current, true)
}, { immediate: true })

watch(selectedDeptGroupRankList, (list) => {
  if (!isCenterLead.value || !list.length) return
  if (props.selectedGroupId && list.some(item => item.orgUnitId === props.selectedGroupId)) return
  const current = list.find(item => item.orgUnitId === props.selectedGroupId) || list[0]
  selectGroup(current, true)
}, { immediate: true })

function onModuleBClick(item) {
  if (isCenterLead.value) {
    emit('selectDept', toDeptPayload(item))
  } else {
    selectGroup(item)
  }
}

function selectGroup(item, auto = false) {
  if (!item) return
  const payload = {
    ...item,
    teamName: item.teamName || item.name,
    orgUnitId: item.orgUnitId || item.id,
  }
  emit('selectGroup', payload)
  if (auto) {
    emit('defaultSelectionReady', {
      dept: isCenterLead.value ? toDeptPayload(moduleBRows.value.find(row => row.id === activeModuleBId.value)) : null,
      group: payload,
    })
  }
}

function toDeptPayload(item) {
  if (!item) return null
  return {
    id: item.deptId || item.id,
    deptId: item.deptId || item.id,
    deptName: item.deptName || item.name,
    name: item.deptName || item.name,
    leaderName: item.leaderName || '-',
  }
}

fetchAccessibleTree()

function teacherRankMeta(item) {
  const parts = [
    item.deptName || groupNameDeptMap.value.get(item.teamName),
    item.teamName,
    `${item.customerCount} 位客户`,
  ].filter(Boolean)
  return parts.join(' · ')
}

function buildGroupDeptMap(nodes) {
  const map = new Map()
  function walk(node, currentDept) {
    const nextDept = node.type === 'DEPT'
      ? { deptId: node.id, deptName: node.name, leaderName: node.leaderName || '-' }
      : currentDept
    if (node.type === 'GROUP') {
      map.set(node.id, nextDept || {
        deptId: `direct-${node.id}`,
        deptName: '中心直属',
        leaderName: '-'
      })
    }
    const children = node.children || []
    children.forEach(child => walk(child, nextDept))
  }
  nodes.forEach(node => walk(node, null))
  return map
}

function formatRate(value) {
  return Math.round(Number(value || 0) * 100) / 100
}

function rateClass(value, low, high, reverse) {
  if (reverse) {
    if (value >= high) return 'insight-rate--danger'
    if (value >= low) return 'insight-rate--warn'
    return 'insight-rate--good'
  }
  if (value < low) return 'insight-rate--danger'
  if (value < high) return 'insight-rate--warn'
  return 'insight-rate--good'
}
</script>

<style scoped>
.insight-rank-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.dash-card__header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dash-panel__heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dash-panel__hint {
  font-size: 11px;
  color: rgba(29, 47, 44, 0.45);
  line-height: 1.2;
}

.insight-rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 13px;
  border-radius: 12px;
  border: 1px solid rgba(29, 47, 44, 0.06);
  background: rgba(255, 250, 244, 0.6);
  transition: background 160ms ease;
}

.insight-rank-item:hover {
  background: rgba(41, 107, 115, 0.04);
}

.insight-rank-item--rank-1 .insight-rank-item__rank {
  background: linear-gradient(135deg, #c79a2e, #f0d58a);
  color: #fff;
  box-shadow: 0 6px 16px rgba(199, 154, 46, 0.22);
}

.insight-rank-item--rank-2 .insight-rank-item__rank {
  background: linear-gradient(135deg, #aeb7bf, #d9e0e6);
  color: #fff;
  box-shadow: 0 6px 16px rgba(140, 150, 160, 0.18);
}

.insight-rank-item--rank-3 .insight-rank-item__rank {
  background: linear-gradient(135deg, #c57b42, #e7b18a);
  color: #fff;
  box-shadow: 0 6px 16px rgba(197, 123, 66, 0.18);
}

.insight-rank-item__rank {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--ink-soft);
  background: rgba(29, 47, 44, 0.05);
  flex-shrink: 0;
}

.insight-rank-item__body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.insight-rank-item__body strong {
  font-size: 0.82rem;
  white-space: nowrap;
}

.insight-rank-item__body span {
  font-size: 0.7rem;
  color: var(--ink-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.insight-rank-item__value {
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--brand);
  flex-shrink: 0;
}

.insight-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.74rem;
  margin-top: 4px;
  table-layout: fixed;
}

.insight-table th {
  padding: 10px 8px;
  text-align: left;
  font-size: 0.66rem;
  font-weight: 700;
  color: var(--ink-soft);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(29, 47, 44, 0.08);
  background: rgba(41, 107, 115, 0.04);
}

.insight-table td {
  padding: 9px 8px;
  border-bottom: 1px solid rgba(29, 47, 44, 0.06);
  vertical-align: middle;
}

.insight-table tr:hover td {
  background: rgba(41, 107, 115, 0.04);
}

.insight-table__row--clickable {
  cursor: pointer;
  transition: background 120ms ease;
}

.insight-table__row--clickable:hover td {
  background: rgba(41, 107, 115, 0.08);
}

.insight-table__row--clickable:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.insight-table__row--clickable:focus-visible td {
  background: rgba(41, 107, 115, 0.08);
}

.insight-table__row--active td {
  background: rgba(41, 107, 115, 0.1);
}

.insight-table th:nth-child(1),
.insight-table td.insight-table__rank {
  width: 42px;
  text-align: center;
}

.insight-table th:nth-child(2),
.insight-table td.insight-table__name {
  width: 29%;
}

.insight-table th:nth-child(3),
.insight-table td.insight-table__leader {
  width: 22%;
}

.insight-table th:nth-child(4),
.insight-table td.insight-table__count {
  width: 66px;
  text-align: center;
}

.insight-table th:nth-child(5),
.insight-table td:nth-child(5) {
  width: 74px;
}

.insight-table th:nth-child(6),
.insight-table td:nth-child(6) {
  width: 74px;
}

.insight-table th:nth-child(5),
.insight-table th:nth-child(6),
.insight-table td:last-child,
.insight-table td:nth-last-child(2) {
  text-align: center;
}

.insight-table td:last-child,
.insight-table td:nth-last-child(2) {
  white-space: nowrap;
}

.insight-table__name,
.insight-table__leader {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insight-rate {
  display: inline-flex;
  justify-content: center;
  min-width: 52px;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 600;
}

.insight-rate--good {
  background: rgba(43, 139, 100, 0.12);
  color: #2b8b64;
}

.insight-rate--warn {
  background: rgba(210, 140, 40, 0.12);
  color: #d28c28;
}

.insight-rate--danger {
  background: rgba(203, 97, 69, 0.12);
  color: #cb6145;
}

.insight-chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 8px;
}

.insight-chart-section h3 {
  margin: 0 0 10px;
  font-size: 0.88rem;
  color: var(--ink);
}

.insight-bar-chart {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.insight-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.insight-bar-label {
  width: 60px;
  font-size: 0.74rem;
  color: var(--ink-soft);
  text-align: right;
  flex-shrink: 0;
}

.insight-bar-track {
  flex: 1;
  height: 16px;
  border-radius: 4px;
  background: rgba(41, 107, 115, 0.06);
  overflow: hidden;
}

.insight-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #296b73, #2b8b64);
  transition: width 400ms ease;
}

.insight-bar-fill--alt {
  background: linear-gradient(90deg, #d28c28, #d3a06c);
}

.insight-bar-value {
  width: 72px;
  font-size: 0.72rem;
  color: var(--ink-soft);
  font-family: "Bahnschrift", "DIN Alternate", "Arial Narrow", sans-serif;
  flex-shrink: 0;
}

@media (max-width: 860px) {
  .insight-chart-grid {
    grid-template-columns: 1fr;
  }

  .insight-table {
    font-size: 0.74rem;
  }

  .insight-table th,
  .insight-table td {
    padding: 7px 6px;
  }

  .insight-rank-item__body {
    flex-wrap: wrap;
  }

  .insight-table {
    table-layout: auto;
  }

  .insight-table th:nth-child(2),
  .insight-table td.insight-table__name,
  .insight-table th:nth-child(3),
  .insight-table td.insight-table__leader {
    width: auto;
  }
}
</style>
